import React, { useRef, useState, forwardRef } from 'react';
import { Button, Icon, Select } from '@iwoca/orion';
import { Trans } from '@lingui/macro';
import clsx from 'clsx';
import { de } from 'date-fns/locale';
import { useLocale } from 'hooks/useLocale';
import DatePicker, {
  CalendarContainer,
  registerLocale,
} from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { TTime } from 'types';
import { DateInput } from './DateInput';

import styles from './DateTimePicker.module.css';

type TDateIntervals = { start: Date; end: Date }[] | undefined;

type TProps = {
  timeOptions?: TTime[];
  showTimeInput?: boolean;
  className?: string;
  includeDateIntervals?: TDateIntervals;
  disableTimeOption?: (id: string, selectedDate: Date | null) => boolean;
  handleChange: (
    contactDate: Date | null,
    contactTime?: string | undefined,
  ) => void;
  filterDate: (date: Date) => boolean;
  withInput?: boolean;
  defaultDate?: string;
};

export const DateTimePicker = ({
  timeOptions,
  showTimeInput = false,
  className,
  includeDateIntervals,
  disableTimeOption,
  filterDate,
  handleChange,
  withInput,
  defaultDate,
}: TProps) => {
  const { i18n, isDE } = useLocale();
  const inputRef = useRef<InstanceType<typeof DatePicker> | null>(null);
  const todaysDate = new Date();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | undefined>('');
  const [timeInputValue, setTimeInputValue] = useState<string | undefined>('');

  registerLocale('de', de);

  const setDate = (date: Date | null) => {
    if (date) {
      setSelectedDate(date);
      setTimeInputValue('');
    }
  };

  const StyledCalendarContainer = ({
    children,
  }: {
    children: React.ReactNode;
  }) => {
    return (
      <CalendarContainer
        data-testid="calendar"
        className={styles.calendarContainer}
      >
        {children}
      </CalendarContainer>
    );
  };

  const longDate = (date: Date) => {
    return i18n.date(date, {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const toggleCalendar = (open: boolean) => {
    if (inputRef.current !== null) {
      inputRef.current.setOpen(open);
    }
  };

  const ContactTimeButton = forwardRef<HTMLButtonElement>((_props, ref) => {
    const dateToDisplay = selectedDate
      ? i18n.date(selectedDate, {
          day: '2-digit',
          month: 'short',
        })
      : null;

    const timeLabelIndexAfterSelected =
      timeOptions && selectedTime
        ? timeOptions.findIndex((x) => x.id === selectedTime)
        : 0;

    const timeToDisplay =
      timeOptions && timeOptions[timeLabelIndexAfterSelected].label;

    return (
      <button
        data-testid="custom-input"
        className={styles.customInput}
        onClick={() => toggleCalendar(true)}
        ref={ref}
      >
        {selectedTime && selectedTime !== 'asap' ? (
          <Trans>
            {dateToDisplay} from {timeToDisplay}
          </Trans>
        ) : (
          <Trans>As soon as possible</Trans>
        )}

        <Icon icon="penFilled" className={styles.inputIcon} />
      </button>
    );
  });

  ContactTimeButton.displayName = 'ContactTimeButton';

  return (
    <DatePicker
      withPortal
      locale={isDE ? de : undefined}
      data-testid="date-picker-portal"
      disabledKeyboardNavigation
      ref={inputRef}
      customInput={
        withInput ? (
          <DateInput
            toggleCalendar={toggleCalendar}
            handleChange={handleChange}
            defaultDate={defaultDate}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
          />
        ) : (
          <ContactTimeButton />
        )
      }
      className={clsx(className, styles.dateTimePicker)}
      selected={selectedDate}
      timeInputLabel=""
      timeFormat="HH:mm"
      onChange={setDate}
      filterDate={filterDate}
      includeDateIntervals={includeDateIntervals ?? undefined}
      calendarStartDay={1}
      dayClassName={(date) => {
        return clsx(styles.day, {
          [styles.todaysDate]: longDate(date) === longDate(todaysDate),
          [styles.selectedDate]:
            selectedDate && longDate(date) === longDate(selectedDate),
          [styles.disabledDate]: !filterDate(date),
        });
      }}
      weekDayClassName={() => styles.weekDay}
      shouldCloseOnSelect={false}
      dateFormat={showTimeInput ? "EE, do MMM 'from' h:mm aa" : 'EE, do MMM'}
      calendarContainer={StyledCalendarContainer}
      renderCustomHeader={({
        monthDate,
        decreaseMonth,
        increaseMonth,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled,
      }) => {
        const monthYearDate = i18n.date(monthDate, {
          month: 'long',
          year: 'numeric',
        });

        return (
          <div className={styles.monthHeader}>
            <button
              data-testid="data-picker-previous-month"
              className={styles.monthButton}
              onClick={decreaseMonth}
              disabled={nextMonthButtonDisabled}
            >
              <Icon icon="angleLeft" />
            </button>
            <p className={styles.monthText}>{monthYearDate}</p>
            <button
              data-testid="data-picker-next-month"
              className={styles.monthButton}
              onClick={increaseMonth}
              disabled={prevMonthButtonDisabled}
            >
              <Icon icon="angleRight" />
            </button>
          </div>
        );
      }}
    >
      {showTimeInput && timeOptions && (
        <div className={styles.timeInputContainer}>
          <Select
            data-testid="time-input"
            onChange={(e) => {
              setTimeInputValue(e.target.value);
            }}
            value={timeInputValue}
          >
            <option disabled value="">
              <Trans>Choose a time</Trans>
            </option>
            {timeOptions.map((time) => {
              return (
                <option
                  key={`${time.id}`}
                  value={time.id}
                  data-testid={time.id}
                  disabled={
                    disableTimeOption
                      ? disableTimeOption(time.id, selectedDate)
                      : false
                  }
                >
                  {time.label}
                </option>
              );
            })}
          </Select>
        </div>
      )}

      <div className={styles.buttonsContainer}>
        <Button
          variant="tertiary"
          className={styles.button}
          onClick={() => {
            setTimeInputValue(selectedTime);
            toggleCalendar(false);
          }}
        >
          <Trans>Cancel</Trans>
        </Button>
        <Button
          variant="primary"
          className={styles.button}
          disabled={
            showTimeInput ? !selectedDate || !timeInputValue : !selectedDate
          }
          onClick={() => {
            setSelectedTime(timeInputValue);
            handleChange(selectedDate, timeInputValue);
            toggleCalendar(false);
          }}
        >
          <Trans>Confirm</Trans>
        </Button>
      </div>
    </DatePicker>
  );
};
